import {useState, useEffect, useRef} from 'react';
import {CheckIcon, ChevronDownIcon} from 'lucide-react';
import {InputSelect} from '@/src/app/data/types/InputTypes';
import * as React from 'react';
import {cn} from '../../../lib/utils';
import {usePopper} from 'react-popper';

type MultipleSelectProps = {
	placeholder: string;
	name: string;
	type?: string;
	register: any;
	onChange: any;
	error?: string;
	className?: string;
	isRequired?: boolean;
	options: InputSelect[] | string[];
}

export default function MultiSelect({
	placeholder,
	name,
	options,
	onChange,
	error,
	className,
	isRequired = false
}: MultipleSelectProps) {
	const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
	const [isOpen, setIsOpen] = useState(false);
	const wrapperRef = useRef<HTMLLabelElement>(null);
	const checkboxRef = useRef<HTMLInputElement>(null);
	const [refEl, setRefEl] = useState<HTMLElement | null>(null);
	const [popperEl, setPopperEl] = useState<HTMLElement | null>(null);
	const {
		styles,
		attributes,
		update
	} = usePopper(refEl, popperEl, {
		placement: 'bottom-start',
		modifiers: [
			{
				name: 'flip',
				options: {fallbackPlacements: ['top-start']}
			}
		],
	});

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
				setIsOpen(false);
				if (checkboxRef.current) {
					checkboxRef.current.checked = false;
				}
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	useEffect(() => {
		if (!update) {
			return;
		}

		const handleScrollResize = () => {
			update();
		};

		window.addEventListener('scroll', handleScrollResize, true);
		window.addEventListener('resize', handleScrollResize);

		return () => {
			window.removeEventListener('scroll', handleScrollResize, true);
			window.removeEventListener('resize', handleScrollResize);
		};
	}, [update]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const isChecked = e.target.checked;
		const optionLabel = e.target.dataset.label!;

		const selectedOptionSet = new Set(selectedOptions);
		if (isChecked) {
			selectedOptionSet.add(optionLabel);
		} else {
			selectedOptionSet.delete(optionLabel);
		}
		const newSelectedOptions = Array.from(selectedOptionSet);
		setSelectedOptions(newSelectedOptions);
		onChange(newSelectedOptions);
	};

	const toggleOpen = () => {
		const newIsOpen = !isOpen;
		setIsOpen(newIsOpen);
		if (checkboxRef.current) {
			checkboxRef.current.checked = newIsOpen;
		}
	};

	return (
		<label className={cn(`relative w-full`, className)} ref={wrapperRef}>
			<input ref={checkboxRef} type="checkbox" className="hidden peer" readOnly/>
			<div
				ref={setRefEl} // pridane sem
				onClick={toggleOpen}
				className={`relative flex items-center gap-1 cursor-pointer w-full bg-white h-[56px] placeholder:text-transparent text-blue text-sm outline outline-1 hover:outline-2  ${
					error ? 'outline-error' : 'outline-grey-300'
				} rounded-[6px] pl-3 pr-7 py-2 transition duration-300 ease-in-out ${
					error ? 'focus:outline-error hover:outline-error' : 'focus:outline-blue  outline-gray-300'
				}`}
			>
				<div className="line-clamp-1">
					{selectedOptions.length > 0 ? (
						selectedOptions.map((option, index) => {
							const isLast = index === selectedOptions.length - 1;
							const haveComma = selectedOptions.length > 1 && !isLast ? ',' : '';
							return <span key={index}>{option}{haveComma} </span>;
						})
					) : (
						<span className=""></span>
					)}
				</div>
				<ChevronDownIcon className="absolute top-[calc(50%_-_8px)] right-[12px] size-4 opacity-70 peer-focus:bg-red-600 peer-checked:bg-red-600"/>
			</div>
			<label className={`pointer-events-none absolute cursor-text bg-white text-[16px] rounded-[6px] px-1 left-2.5 transition-all transform origin-left
				peer-checked:-top-2 peer-checked:text-sm peer-focus:-top-2  peer-focus:text-sm
				${error ? 'text-error peer-focus:text-error' : 'text-gray-500 peer-focus:text-blue'}
          		${selectedOptions.length > 0 ? '-top-2 text-sm' : 'top-[16px] text-[16px]'}
        	`}
			>
				{placeholder}
				{isRequired ? <span> *</span> : ''}
			</label>
			{error && (
				<p className="mt-[6px] px-[16px] text-[12px] text-error leading-3">{error}</p>
			)}
			<div
				ref={setPopperEl}
				style={styles.popper}
				{...attributes.popper}
				className={`absolute w-[300px] bg-white border outline-gray-200 max-h-60 overflow-y-scroll transition-opacity z-10 ${
					isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
				}`}
			>
				<ul className="w-full">
					{options.map((option, index) => {
						const opt = option as InputSelect;
						return (
							<li className="w-full" key={index}>
								<label htmlFor={`${name}-${index}`} className="w-full flex items-center whitespace-nowrap cursor-pointer px-2 py-1 select-none">
									<input type="checkbox" id={`${name}-${index}`} name={name} value={opt.value} className="peer hidden" onChange={handleChange}
										   data-label={opt.label}/>
									<span
										className="w-4 h-4 flex items-center justify-center border-2 border-black rounded-[2px]
										peer-checked:bg-black peer-checked:border-black transition-colors duration-200"
									>
                  					  <CheckIcon className={`size-3.5 text-white`} style={{strokeWidth: 4}}/>
                  					</span>
									<span className="ml-2">{opt.label}</span>
								</label>
							</li>
						);
					})}
				</ul>
			</div>
		</label>
	);
}
